<template>
    <main class="layout__main--narrow layout__main--no-top-padding">
        <BaseHeader
            v-if="pending || headerImage"
            :placeholder="pending"
            :image="headerImage"
        />

        <transition name="fade">
            <article v-if="!error && !notFound && !pending">
                <BaseSection>
                    <template #title>
                        <h1>{{ page.title }}</h1>
                    </template>

                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        v-if="page.introText"
                        class="lead"
                        data-allow-anchor
                        v-html="page.introText"
                    />
                    <!-- eslint-enable vue/no-v-html -->
                </BaseSection>
                <BaseSection
                    v-if="page.contactOptions && page.contactOptions.length"
                    ref="form"
                >
                    <template
                        v-if="formSuccess"
                        #title
                    >
                        <h2>Message sent</h2>
                    </template>

                    <Form
                        v-if="!formSuccess"
                        v-slot="{ errors, handleSubmit }"
                    >
                        <form
                            ref="validator"
                            @submit.prevent="handleSubmit(onSubmit())"
                        >
                            <BaseFormField v-if="formSuccess === false">
                                <span class="error">
                                    Something went wrong submitting the form. Please try again.
                                </span>
                            </BaseFormField>

                            <Field
                                v-slot="{ meta }"
                                v-model="formData.name"
                                rules="required"
                                name="name"
                                as=""
                            >
                                <BaseFormField
                                    :required="true"
                                    :errors="getErrors(errors, 'name')"
                                    :data-dirty="meta.dirty"
                                >
                                    <template #label>
                                        <label for="name">Name</label>
                                    </template>

                                    <BaseInput
                                        id="name"
                                        v-model="formData.name"
                                        name="name"
                                        type="text"
                                    />
                                </BaseFormField>
                            </Field>

                            <Field
                                v-slot="{ meta }"
                                v-model="formData.email"
                                rules="required|email"
                                name="e-mail"
                                as=""
                            >
                                <BaseFormField
                                    :required="true"
                                    :errors="getErrors(errors, 'e-mail')"
                                    :data-dirty="meta.dirty"
                                >
                                    <template #label>
                                        <label for="user-email">E-mail</label>
                                    </template>

                                    <template #default>
                                        <BaseInput
                                            id="email"
                                            v-model="formData.email"
                                            type="email"
                                        />
                                    </template>
                                </BaseFormField>
                            </Field>

                            <Field
                                v-slot="{ meta }"
                                v-model="formData.emailTo"
                                name="subject"
                                rules="required"
                                as=""
                            >
                                <BaseFormField
                                    :required="true"
                                    :errors="getErrors(errors, 'subject')"
                                    :data-dirty="meta.dirty"
                                >
                                    <template #label>
                                        <label for="subject">My message is about</label>
                                    </template>

                                    <template #default>
                                        <BaseSelect
                                            id="subject"
                                            v-model="formData.emailTo"
                                            :options="contactOptions"
                                            placeholder="Choose"
                                        />
                                    </template>

                                    <template #info>
                                        This will help us direct your message to the right person
                                    </template>
                                </BaseFormField>
                            </Field>

                            <Field
                                v-slot="{ meta }"
                                v-model="formData.message"
                                name="message"
                                rules="required"
                                as=""
                            >
                                <BaseFormField
                                    :required="true"
                                    :errors="getErrors(errors, 'message')"
                                    :data-dirty="meta.dirty"
                                >
                                    <template #label>
                                        <label for="message">Message</label>
                                    </template>

                                    <template #default>
                                        <BaseTextarea
                                            id="message"
                                            v-model="formData.message"
                                            type="text"
                                            placeholder="Write your message here"
                                            rows="3"
                                        />
                                    </template>
                                </BaseFormField>
                            </Field>

                            <BaseFormField>
                                <vue-hcaptcha
                                    ref="captcha"
                                    sitekey="98b8145c-44ea-4356-b15d-d0d2688012e8"
                                    @verify="onCaptchaVerify($event)"
                                    @expire="onCaptchaExpire"
                                />
                            </BaseFormField>

                            <BaseFormField>
                                <BaseButton :disabled="isSending">
                                    <template v-if="!isSending">Send message</template>
                                    <template v-else>Sending message...</template>
                                </BaseButton>
                            </BaseFormField>
                        </form>
                    </Form>
                    <p v-else>We'll get back to you as soon as we can.</p>
                </BaseSection>
                <template v-if="page.faq && page.faq.length">
                    <BaseSection
                        v-for="(faq, index) in page.faq"
                        :key="`faq${index}`"
                        class="section--extra-spacing"
                    >
                        <template
                            v-if="faq.title"
                            #title
                        >
                            <h2>{{ faq.title }}</h2>
                        </template>

                        <BaseAccordion
                            v-if="faq.items"
                            :items="faq.items"
                        >
                            <template #title="{ item }">
                                {{ item.blockTitle }}
                            </template>

                            <template #default="{ item }">
                                <!-- eslint-disable vue/no-v-html -->
                                <div
                                    data-allow-anchor
                                    v-html="item.richText"
                                />
                                <!-- eslint-enable vue/no-v-html -->
                            </template>
                        </BaseAccordion>
                    </BaseSection>
                </template>

                <BaseSection class="section--extra-spacing">
                    <template #title>
                        <h2>Postal address</h2>
                    </template>

                    <p>
                        EAU Central Office<br>
                        PO Box 30016<br>
                        NL-6803 AA ARNHEM<br>
                        The Netherlands
                    </p>
                </BaseSection>
            </article>

            <article v-else-if="pending">
                <BaseSection>
                    <template #title>
                        <h1>
                            <ContentPlaceholdersHeading :lines="2" />
                        </h1>
                    </template>

                    <p class="lead">
                        <ContentPlaceholdersText :lines="5" />
                    </p>
                </BaseSection>

                <BaseSection>
                    <ContentPlaceholdersImg />
                </BaseSection>
            </article>

            <BaseError
                v-else-if="error || notFound"
                :error="error || { statusCode: 404 }"
            />
        </transition>

        <!-- Livechat widget -->
        <client-only>
            <noscript>
                <a href="https://www.livechat.com/chat-with/12060225/" rel="nofollow">Chat with us</a>, powered by <a href="https://www.livechat.com/?welcome" rel="noopener nofollow" target="_blank">LiveChat</a>
            </noscript>
        </client-only>
    </main>
</template>

<script setup>
import ContactQuery from '~/graphql/queries/Contact.graphql';
import {computed, nextTick, ref} from 'vue';
import {defineRule, Field, Form} from 'vee-validate';
import {email, required} from '@vee-validate/rules';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';

const config = useRuntimeConfig();
const { activeSite } = useMultisite();

defineRule('required', required);
defineRule('email', email);

const getErrors = (errors, name) => {
    if (!errors[name]) {
        return [];
    }

    let errorMessage = 'This field is required';

    if (name === 'e-mail') {
        errorMessage = 'Please provide a correct e-mail address';
    }

    return [errorMessage];
};

const { variables }  = useCraftVariables();

const response = await useLazyAsyncQuery({
    query: ContactQuery,
    variables
});

const {
    craftEntry: page,
    headerImage,
    pending,
    error
} = usePageQuery(
    response,
    { entryKey: 'contact', activeSite }
);

const formData = ref({
    name: null,
    email: null,
    emailTo: '',
    message: null,
    hCaptcha: null
});

const captchaSuccess = ref(false);
const responseMessage = ref(null);
const formSuccess = ref(undefined);
const isSending = ref(false);

function onCaptchaVerify(captchaResponse) {
    captchaSuccess.value = true;
    formData.value.hCaptcha = captchaResponse;
}

function onCaptchaExpire() {
    captchaSuccess.value = false;
    formData.value.hCaptcha = null;
}

const contactOptions = computed(() => {
    if(!page?.value?.contactOptions) {
        return [];
    }
    return page?.value?.contactOptions.map((option) => ({
        value: option.contactEmail,
        label: option.contactTitle
    }));
});

const form = ref();

const onSubmit = async() => {
    if (!captchaSuccess.value) {
        alert('Sorry! You didn\'t pass the anti-bot test. Please reload the page and try again');
        onCaptchaExpire();
        isSending.value = false;
        return;
    }

    const submitResponse = await $fetch(`${config.public.apiBaseURL}/contact/submit`, {
        method: 'POST',
        body: JSON.stringify(formData.value)
    });

    responseMessage.value = submitResponse.message;
    formSuccess.value = submitResponse.send;
    isSending.value = false;

    // TODO: Scroll to success or error message
    if (form.value) {
        nextTick(() => {
            form.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }
};
</script>
